import { Link } from "react-router-dom";
import { Check, Sparkles, Target, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/shared/Reveal";
import { PrimaryButton, SecondaryButton } from "@/components/shared/Buttons";
import { useSeo } from "@/hooks/useSeo";
import { ABOUT, ASSETS } from "@/lib/site";

export default function About() {
  useSeo(
    "About ASAI",
    "ASAI – Atmasamman School of AI is an industry-focused education, internship and career-development platform bridging academic learning and industry expectations."
  );

  return (
    <div data-testid="page-about">
      <PageHeader
        overline="About ASAI"
        titleLines={["An industry-focused", "school for AI careers"]}
        description={ABOUT.intro}
      />

      {/* Team teaser — moved to top so visitors meet the team early */}
      <section className="bg-navy py-20 md:py-24">
        <div className="container-asai flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <SectionHeading onDark overline="The people" title="Meet the team behind ASAI" />
          <Link to="/team" data-testid="about-team-link" className="inline-flex items-center gap-2 rounded-full bg-orange px-7 py-3.5 text-sm font-medium text-white transition-transform hover:scale-105">
            Meet the Team <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* What we teach */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-asai grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-navy/10 bg-navy">
              <img
                src="/generated/practical-learning.png"
                alt="Modern coding workspace showing a live editor, analytics dashboard and holographic Python, AI and cloud icons — representing ASAI's hands-on, industry-oriented learning"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
          <div className="flex flex-col gap-6">
            <SectionHeading overline="What we teach" title="Practical, industry-oriented learning" />
            <p className="text-navy/70">
              ASAI provides hands-on, industry-oriented learning across the technologies that power
              today&apos;s careers — designed to make you job-ready, not just exam-ready.
            </p>
            <StaggerGroup className="flex flex-wrap gap-2">
              {ABOUT.skills.map((s) => (
                <StaggerItem key={s} className="rounded-full border border-navy/12 bg-lightgrey px-4 py-2 text-sm font-medium text-navy">
                  {s}
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-navy py-24 md:py-32">
        <div className="container-asai grid gap-6 md:grid-cols-2">
          <Reveal className="flex flex-col gap-5 rounded-3xl border border-white/10 bg-white/[0.03] p-9">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-royal/15 text-orange"><Sparkles className="h-6 w-6" /></span>
            <h2 className="font-heading text-3xl text-white">Our Vision</h2>
            <p className="text-white/70">{ABOUT.vision}</p>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col gap-5 rounded-3xl border border-white/10 bg-white/[0.03] p-9">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-royal/15 text-orange"><Target className="h-6 w-6" /></span>
            <h2 className="font-heading text-3xl text-white">Our Mission</h2>
            <p className="text-white/70">{ABOUT.mission}</p>
          </Reveal>
        </div>
      </section>

      {/* What makes ASAI different */}
      <section className="bg-lightgrey py-24 md:py-32">
        <div className="container-asai">
          <SectionHeading overline="The difference" title="What makes ASAI different" description="An industry-focused platform built around outcomes and real capability." />
          <StaggerGroup className="mt-14 grid gap-6 sm:grid-cols-2">
            {ABOUT.different.map((d) => (
              <StaggerItem key={d.title} className="rounded-2xl border border-navy/10 bg-white p-8">
                <h3 className="font-heading text-2xl text-navy">{d.title}</h3>
                <p className="mt-3 text-navy/65">{d.text}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Learning methodology */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-asai">
          <SectionHeading overline="How we teach" title="Our learning methodology" />
          <StaggerGroup className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ABOUT.methodology.map((m) => (
              <StaggerItem key={m.step} className="flex flex-col gap-3 rounded-2xl border border-navy/10 bg-lightgrey p-7">
                <span className="font-heading text-4xl font-bold text-royal">{m.step}</span>
                <h3 className="font-heading text-xl text-navy">{m.title}</h3>
                <p className="text-sm text-navy/65">{m.text}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Who we support */}
      <section className="bg-navy py-24 md:py-32">
        <div className="container-asai">
          <SectionHeading onDark overline="Who we support" title="Built for every kind of learner" />
          <StaggerGroup className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ABOUT.whoWeSupport.map((w) => (
              <StaggerItem key={w.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-8">
                <h3 className="font-heading text-xl text-white">{w.title}</h3>
                <p className="mt-3 text-sm text-white/60">{w.text}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Collaboration */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-asai grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="flex flex-col gap-6">
            <SectionHeading overline="Collaboration" title="Industry and college collaboration" />
            <p className="text-lg text-navy/70">{ABOUT.collaboration}</p>
            <div className="flex flex-wrap gap-3">
              <PrimaryButton to="/college-partnerships" withArrow testId="about-partner-btn">Partner with ASAI</PrimaryButton>
              <SecondaryButton to="/expert-sessions" testId="about-expert-btn">Expert Sessions</SecondaryButton>
            </div>
          </div>
          <Reveal className="overflow-hidden rounded-3xl border border-navy/10">
            <img src={ASSETS.robotic} alt="Technology collaboration" className="aspect-[4/3] w-full object-cover" />
          </Reveal>
        </div>
      </section>

      {/* Career development approach */}
      <section className="bg-lightgrey py-24 md:py-32">
        <div className="container-asai grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading overline="Careers first" title="Our career development approach" />
          <ul className="grid gap-4 sm:grid-cols-2">
            {ABOUT.careerApproach.map((c) => (
              <li key={c} className="flex items-start gap-3 rounded-xl border border-navy/10 bg-white p-4">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-royal/10 text-royal"><Check className="h-3.5 w-3.5" /></span>
                <span className="text-sm font-medium text-navy">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
